/**
 * "Aparência dos seus agentes" (docs/refactor/25-agentes-no-escritorio.md): how the person's AI agents look in the
 * office — Sorteio (the default: a random family, model and colour per agent), Uma família (random within it) or
 * Fixa (every agent the same) — with the result drawn before saving. Next to "Meu personagem": the person's looks
 * live in one place. Appearance only; nothing here changes what an agent does.
 */

import { Link } from 'react-router-dom'
import { HATS, PREMIUM_ITEMS, type HatKind } from '../bughunt/wardrobe'
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { usePrefersReducedMotion } from '../lib/theme'
import { AgentAvatar } from '../bughunt/AgentFigures'
import {
  AGENT_COLORS, AGENT_STATES, FAMILIES, FAMILY_IDS, agentLookFor, parsePreference,
  type AgentFamily, type AgentLook, type AgentLookPreference, type AgentState,
} from '../bughunt/agentLooks'

/** One agent drawn small, for the picker. */
export function AgentPreview({ look, state = 'working', size = 84, label }: { look: AgentLook; state?: AgentState; size?: number; label?: string }) {
  const still = usePrefersReducedMotion()
  return (
    <svg className={`bh-ag-preview${still ? ' bh-ag-preview--still' : ''}`} viewBox="-30 -40 60 68" width={size} height={size * (68 / 60)} role="img" aria-label={label}>
      <AgentAvatar look={look} state={state} />
    </svg>
  )
}

const SAMPLES = ['amostra-1', 'amostra-2', 'amostra-3', 'amostra-4']

export function AgentLooksPicker({ onSaved }: { onSaved?: () => void }) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const saved = useMemo(() => parsePreference(me?.agentLooks), [me?.agentLooks])
  const [draft, setDraft] = useState<AgentLookPreference | null>(null)
  const current = draft ?? saved
  const [family, setFamily] = useState<AgentFamily>(current.family ?? current.look?.family ?? 'robot')
  const fixed = current.mode === 'fixed' ? current.look! : null

  const save = useMutation({
    mutationFn: () => api.me.agentLooks(current.mode === 'random' && !current.hat ? null : JSON.stringify(current)),
    onSuccess: () => {
      setDraft(null)
      for (const key of ['me', 'bug-hunt']) void queryClient.invalidateQueries({ queryKey: [key] })
      onSaved?.()
    },
  })

  if (!me?.developerId) return null
  // The hat rides along whatever the mode (docs/refactor/47).
  const choose = (next: AgentLookPreference) => setDraft({ ...next, ...(current.hat && next.hat === undefined ? { hat: current.hat } : {}) })
  const pickHat = (hat: HatKind) => setDraft({ ...current, hat: hat === 'none' ? undefined : hat })
  const refusedForPlan = save.error instanceof ApiError && save.error.code === 'plan_limit'
  const pickFixed = (look: AgentLook) => choose({ mode: 'fixed', look })

  return (
    <section className="stack agent-looks" aria-label={t('agentLooks.title')}>
      <div className="stack" style={{ gap: 2 }}>
        <strong>{t('agentLooks.title')}</strong>
        <span className="tiny muted">{t('agentLooks.hint')}</span>
      </div>

      <div className="segmented" role="radiogroup" aria-label={t('agentLooks.mode')}>
        {(['random', 'family', 'fixed'] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={current.mode === mode}
            className={`segmented__item${current.mode === mode ? ' segmented__item--active' : ''}`}
            onClick={() => choose(mode === 'random' ? { mode } : mode === 'family' ? { mode, family } : { mode, look: fixed ?? agentLookFor('fixa', { mode: 'family', family }) })}
          >
            {t(`agentLooks.mode.${mode}`)}
          </button>
        ))}
      </div>

      {current.mode !== 'random' && (
        <div className="row row--tight row--wrap" role="radiogroup" aria-label={t('agentLooks.family')}>
          {FAMILY_IDS.map((id) => {
            const on = current.mode === 'family' ? current.family === id : fixed?.family === id
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={on}
                className={`agent-looks__family${on ? ' is-on' : ''}`}
                onClick={() => {
                  setFamily(id)
                  if (current.mode === 'family') choose({ mode: 'family', family: id })
                  else pickFixed({ family: id, model: FAMILIES[id].models[0]!.id, color: FAMILIES[id].models[0]!.color })
                }}
              >
                <AgentPreview look={{ family: id, model: FAMILIES[id].models[0]!.id, color: FAMILIES[id].models[0]!.color }} size={56} />
                <span className="small">{t(`agentLooks.family.${id}`)}</span>
              </button>
            )
          })}
        </div>
      )}

      {fixed && (
        <>
          <div className="agent-looks__models" role="radiogroup" aria-label={t('agentLooks.model')}>
            {FAMILIES[fixed.family].models.map((model) => {
              const on = fixed.model === model.id
              return (
                <button
                  key={model.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  aria-label={model.name}
                  className={`agent-looks__model${on ? ' is-on' : ''}`}
                  onClick={() => pickFixed({ family: fixed.family, model: model.id, color: model.color })}
                >
                  <AgentPreview look={{ family: fixed.family, model: model.id, color: model.color }} size={64} />
                  <span className="tiny">{model.name}</span>
                </button>
              )
            })}
          </div>
          <div className="row row--tight row--wrap" role="radiogroup" aria-label={t('agentLooks.color')}>
            {Object.entries(AGENT_COLORS).map(([id, hex]) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={fixed.color === id}
                aria-label={t(`agentLooks.color.${id}`)}
                className={`agent-looks__color${fixed.color === id ? ' is-on' : ''}`}
                style={{ background: hex }}
                onClick={() => pickFixed({ ...fixed, color: id })}
              />
            ))}
          </div>
        </>
      )}

      {/* A hat for the agents (docs/refactor/47): tried on here; the Pro ones are saved on Pro and Team. */}
      <div className="stack" style={{ gap: 4 }}>
        <span className="tiny muted">{t('character.hat')}</span>
        <div className="row row--tight row--wrap" role="radiogroup" aria-label={t('character.hat')}>
          {HATS.map((hat) => (
            <button
              key={hat}
              type="button"
              role="radio"
              aria-checked={(current.hat ?? 'none') === hat}
              className={`btn btn--sm${(current.hat ?? 'none') === hat ? ' btn--primary' : ''}`}
              onClick={() => pickHat(hat)}
            >
              {t(`character.hat.${hat}`)}
              {PREMIUM_ITEMS.has(hat) && <span className="tiny muted"> · {t('character.pro')}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* What it will look like: a few agents as they would appear, and the states every agent shows. */}
      <div className="stack" style={{ gap: 4 }}>
        <span className="tiny muted">{t('agentLooks.preview')}</span>
        <div className="row row--tight row--wrap agent-looks__preview" data-testid="agent-looks-preview">
          {SAMPLES.map((seed) => {
            const look = agentLookFor(seed, current)
            return <AgentPreview key={seed} look={look} label={FAMILIES[look.family].models.find((m) => m.id === look.model)?.name} />
          })}
        </div>
        <div className="row row--tight row--wrap">
          {AGENT_STATES.map((state) => (
            <span key={state} className="stack agent-looks__state">
              <AgentPreview look={agentLookFor(SAMPLES[0]!, current)} state={state} size={52} />
              <span className="tiny muted">{t(`agentLooks.state.${state}`)}</span>
            </span>
          ))}
        </div>
      </div>

      {refusedForPlan && (
        <div className="notice notice--warn small" role="status">
          {t('character.proTrying')} <Link to="/settings/billing">{t('character.proPlans')}</Link>
        </div>
      )}
      <div className="row row--tight">
        <span className="spacer" />
        {draft && <button type="button" className="btn btn--sm btn--ghost" onClick={() => setDraft(null)}>{t('common.cancel')}</button>}
        <button type="button" className="btn btn--sm btn--primary" disabled={!draft || save.isPending} onClick={() => save.mutate()}>
          {t('agentLooks.save')}
        </button>
      </div>
    </section>
  )
}
